import { useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { DateTime } from 'luxon'
import { ACCENT_BLUE } from 'utils/brand-colors'
import {
  useCreateSetupIntentMutation,
  useGetSubscriptionInvoicesQuery,
  useUpdatePaymentMethodMutation,
} from 'store/subscriptions/subscriptions.api'
import { SubscriptionInvoice } from 'store/subscriptions/subscriptions.types'
import StripeUpdateCardDialog from 'components/subscription/stripe-update-card-dialog.component'

const NAVY = '#12355b'
const MUTED = '#6b7280'
const BORDER = '#d7e3ee'

const formatInvoiceDate = (value?: string | null) => {
  if (!value) return '—'
  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed.toFormat('MMM dd, yyyy') : '—'
}

const formatInvoiceAmount = (invoice: SubscriptionInvoice) => {
  if (typeof invoice.amount === 'number') {
    return (invoice.amount / 100).toFixed(2)
  }
  return invoice.formattedAmount?.replace(/\s*[A-Z]{3}$/, '') || '—'
}

const statusLabel = (status?: string | null) => {
  if (!status) return '—'
  const normalized = status.toLowerCase()
  if (normalized === 'uncollectible') return 'Failed'
  if (normalized === 'past_due' || normalized === 'unpaid') return 'Unpaid'
  if (normalized === 'open') return 'Open'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const statusStyles = (status?: string | null) => {
  const normalized = status?.toLowerCase()
  if (normalized === 'paid') {
    return { bgcolor: '#dcfce7', color: '#166534' }
  }
  if (normalized === 'uncollectible' || normalized === 'void') {
    return { bgcolor: '#fee2e2', color: '#991b1b' }
  }
  if (normalized === 'open' || normalized === 'past_due' || normalized === 'unpaid') {
    return { bgcolor: '#fef3c7', color: '#92400e' }
  }
  return { bgcolor: '#e5e7eb', color: '#374151' }
}

const openInNewTab = (url?: string | null) => {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

const invoiceYears = (createdAt?: string | null) => {
  const currentYear = DateTime.now().year
  const createdYear = createdAt ? DateTime.fromISO(createdAt).year : currentYear
  const startYear = Math.min(
    Number.isFinite(createdYear) ? createdYear : currentYear,
    currentYear - 4
  )
  const years: number[] = []
  for (let year = currentYear; year >= startYear; year -= 1) {
    years.push(year)
  }
  return years
}

type SettingsBillingInvoicesProps = {
  userId?: string
  createdAt?: string | null
  year: number
  onYearChange: (year: number) => void
}

const isRetryableInvoice = (status?: string | null) => {
  const normalized = status?.toLowerCase()
  return normalized === 'open' || normalized === 'past_due' || normalized === 'unpaid'
}

const SettingsBillingInvoices = ({
  userId,
  createdAt,
  year,
  onYearChange,
}: SettingsBillingInvoicesProps) => {
  const years = invoiceYears(createdAt)
  const { data: invoices = [], isLoading, isError } =
    useGetSubscriptionInvoicesQuery(
      { userId: userId || '', year },
      { skip: !userId }
    )
  const [createSetupIntent, { isLoading: creatingSetupIntent }] =
    useCreateSetupIntentMutation()
  const [updatePaymentMethod, { isLoading: updatingPaymentMethod }] =
    useUpdatePaymentMethodMutation()
  const [updateOpen, setUpdateOpen] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<SubscriptionInvoice | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [publishableKey, setPublishableKey] = useState<string | null>(null)
  const [updateError, setUpdateError] = useState<string | null>(null)

  const handleUpdateCard = async (invoice: SubscriptionInvoice) => {
    if (!userId) return
    setUpdateError(null)
    setSelectedInvoice(invoice)
    try {
      const result = await createSetupIntent({ userId }).unwrap()
      if (!result.clientSecret) {
        setUpdateError('Unable to start card update. Please try again.')
        return
      }
      setClientSecret(result.clientSecret)
      setPublishableKey(result.publishableKey || null)
      setUpdateOpen(true)
    } catch {
      setUpdateError('Unable to start card update. Please try again.')
    }
  }

  const handleSaveCard = async (paymentMethodId: string) => {
    if (!userId) return
    await updatePaymentMethod({
      userId,
      paymentMethodId,
      invoiceId: selectedInvoice?.invoiceId,
    }).unwrap()
    setUpdateOpen(false)
    setClientSecret(null)
    setPublishableKey(null)
    setSelectedInvoice(null)
  }

  const handleCloseUpdate = () => {
    if (updatingPaymentMethod) return
    setUpdateOpen(false)
    setClientSecret(null)
    setPublishableKey(null)
    setSelectedInvoice(null)
    setUpdateError(null)
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={700} fontSize={22} color={NAVY}>
            Invoices
          </Typography>
          <Typography color={MUTED} mt={0.25}>
            View and download your monthly subscription invoices.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={year}
            onChange={(event) => onYearChange(Number(event.target.value))}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonthOutlinedIcon sx={{ color: ACCENT_BLUE, fontSize: 18 }} />
                {selected}
              </Box>
            )}
            sx={{
              borderRadius: '10px',
              bgcolor: '#fff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
            }}
          >
            {years.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} />
        </Box>
      )}

      {isError && (
        <Typography color="error">
          Unable to load invoices. Please try again.
        </Typography>
      )}

      {updateError && !updateOpen && (
        <Typography color="error" mb={2}>
          {updateError}
        </Typography>
      )}

      {!isLoading && !isError && (
        <Box
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: '16px',
            overflow: 'auto',
            bgcolor: '#fff',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {[
                  'Date',
                  'Status',
                  'Amount (CAD)',
                  'Details',
                  'Invoice #',
                  'Actions',
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      color: MUTED,
                      borderBottom: `1px solid ${BORDER}`,
                      fontWeight: 500,
                      bgcolor: '#f8fafc',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center', color: MUTED }}>
                    No invoices found for {year}.
                  </TableCell>
                </TableRow>
              )}
              {invoices.map((invoice) => (
                <TableRow key={invoice.invoiceId || invoice.invoiceNumber}>
                  <TableCell sx={{ color: NAVY, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatInvoiceDate(invoice.date)}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.25,
                        py: 0.25,
                        borderRadius: '999px',
                        fontSize: 13,
                        fontWeight: 600,
                        ...statusStyles(invoice.status),
                      }}
                    >
                      {statusLabel(invoice.status)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: NAVY, fontWeight: 600 }}>
                    {formatInvoiceAmount(invoice)}
                  </TableCell>
                  <TableCell sx={{ color: NAVY }}>
                    {invoice.details || '—'}
                  </TableCell>
                  <TableCell sx={{ color: NAVY, fontWeight: 600 }}>
                    {invoice.invoiceNumber || '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!invoice.viewInvoiceUrl}
                        startIcon={<VisibilityOutlinedIcon />}
                        onClick={() => openInNewTab(invoice.viewInvoiceUrl)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!invoice.downloadInvoiceUrl}
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={() => openInNewTab(invoice.downloadInvoiceUrl)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                        }}
                      >
                        Download
                      </Button>
                      {isRetryableInvoice(invoice.status) && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={!userId || creatingSetupIntent}
                          startIcon={<CreditCardOutlinedIcon />}
                          onClick={() => handleUpdateCard(invoice)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Update payment details
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <StripeUpdateCardDialog
        open={updateOpen}
        publishableKey={publishableKey}
        clientSecret={clientSecret}
        retryInvoice={isRetryableInvoice(selectedInvoice?.status)}
        submitting={updatingPaymentMethod}
        errorMessage={updateOpen ? updateError : null}
        onClose={handleCloseUpdate}
        onConfirm={handleSaveCard}
      />
    </Box>
  )
}

export default SettingsBillingInvoices
